// src/pages/TherapistToolsPage.js
import React, { useState, useEffect } from "react";
import API from "../services/api";

// Simple capitalizer
function capitalize(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

function TherapistToolsPage() {
  // ------------------------------
  // RENAME-A-CLIENT States
  // ------------------------------
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [status, setStatus] = useState("");

  // ------------------------------
  // PASSWORD CHANGE States
  // ------------------------------
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwStatus, setPwStatus] = useState("");

  // ------------------------------
  // DEFAULT WELCOME MESSAGE States
  // ------------------------------
  const [welcomeMsg, setWelcomeMsg] = useState("");
  const [wmStatus, setWmStatus] = useState("");
  const [wmSaving, setWmSaving] = useState(false);

  // Fetch clients on mount
  useEffect(() => {
    fetchClients();
    fetchWelcomeMessage();
  }, []);

  const fetchClients = async () => {
    try {
      const res = await API.get("/therapist/clients");
      // Sort them by client_name if desired:
      const sorted = res.data.sort((a, b) =>
        a.client_name.localeCompare(b.client_name)
      );
      setClients(sorted);
    } catch (err) {
      console.error("Error fetching clients:", err);
    }
  };

  // --------------------------------------
  // 1) RENAME CLIENT
  // --------------------------------------
  const handleRename = async () => {
    if (!selectedClientId) {
      alert("Please select a client.");
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      alert("Please fill both first and last name fields.");
      return;
    }

    // Capitalize and form "Last, First"
    const finalFirst = capitalize(firstName.trim());
    const finalLast = capitalize(lastName.trim());
    const combinedName = `${finalLast}, ${finalFirst}`;

    try {
      await API.put(`/therapist/clients/${selectedClientId}`, {
        clientName: combinedName,
      });
      setStatus("Client name updated successfully.");
      // Clear fields
      setFirstName("");
      setLastName("");
      setSelectedClientId("");
      // Re-fetch the client list
      fetchClients();
    } catch (err) {
      console.error("Error updating client name:", err);
      setStatus("Error updating client name.");
    }
  };

  const fetchWelcomeMessage = async () => {
    try {
      const res = await API.get("/therapist/profile");
      setWelcomeMsg(res.data.default_welcome_message || "");
    } catch (err) {
      console.error("Error fetching welcome message:", err);
    }
  };

  const handleSaveWelcomeMessage = async () => {
    setWmStatus("");
    setWmSaving(true);
    try {
      await API.put("/therapist/profile", { defaultWelcomeMessage: welcomeMsg });
      setWmStatus("Default welcome message saved.");
    } catch (err) {
      console.error("Error saving welcome message:", err);
      setWmStatus("Error saving message.");
    } finally {
      setWmSaving(false);
    }
  };

  // --------------------------------------
  // 2) CHANGE PASSWORD
  // --------------------------------------
  const handleChangePassword = async () => {
    setPwStatus("");

    if (!oldPassword || !newPassword) {
      alert("Please fill out all password fields.");
      return;
    }
    if (newPassword !== confirmPassword) {
      alert("New password and Confirm password do not match.");
      return;
    }

    try {
      await API.put("/therapist/password", {
        oldPassword,
        newPassword,
      });
      setPwStatus("Password changed successfully!");
      // Optionally clear fields or force logout
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      // e.g. localStorage.clear(); window.location.reload();
    } catch (err) {
      console.error("Error changing password:", err);
      if (err.response && err.response.data && err.response.data.message) {
        setPwStatus(`Error: ${err.response.data.message}`);
      } else {
        setPwStatus("Error changing password");
      }
    }
  };

  return (
    <div style={{ padding: "1rem" }}>
      <h1>Therapist Tools</h1>

      {/* -----------------------------
          RENAME A CLIENT
       ----------------------------- */}
      <div
        style={{
          border: "1px solid var(--border-color)",
          padding: "1rem",
          marginBottom: "2rem",
          background: "#fff",
        }}
      >
        <h2>Rename a Client</h2>

        <div style={{ marginBottom: "1rem" }}>
          <label>Select a client:</label>
          <br />
          <select
            value={selectedClientId}
            onChange={(e) => setSelectedClientId(e.target.value)}
          >
            <option value="">-- Select Client --</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.client_name}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: "1rem" }}>
          <label>First Name:</label>
          <br />
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </div>

        <div style={{ marginBottom: "1rem" }}>
          <label>Last Name:</label>
          <br />
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </div>

        <button onClick={handleRename}>Update Client Name</button>

        {status && <p style={{ marginTop: "1rem" }}>{status}</p>}
      </div>

      {/* -----------------------------
          CHANGE PASSWORD
       ----------------------------- */}
      <div
        style={{
          border: "1px solid var(--border-color)",
          padding: "1rem",
          background: "#fff",
        }}
      >
        <h2>Change Password</h2>

        <div style={{ marginBottom: "1rem" }}>
          <label>Old Password:</label>
          <br />
          <input
            type="password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
          />
        </div>

        <div style={{ marginBottom: "1rem" }}>
          <label>New Password:</label>
          <br />
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </div>

        <div style={{ marginBottom: "1rem" }}>
          <label>Confirm New Password:</label>
          <br />
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </div>

        <button onClick={handleChangePassword}>Update Password</button>

        {pwStatus && <p style={{ marginTop: "1rem" }}>{pwStatus}</p>}
      </div>

      {/* -----------------------------
          DEFAULT WELCOME MESSAGE
       ----------------------------- */}
      <div
        style={{
          border: "1px solid var(--border-color)",
          padding: "1rem",
          marginTop: "2rem",
          background: "#fff",
        }}
      >
        <h2>Default Welcome Message</h2>
        <p style={{ fontSize: "0.9rem", color: "var(--text-strong)", marginBottom: "0.75rem" }}>
          This message will pre-fill the compose window when you send a welcome
          email to a new client. You can still edit it before sending.
        </p>

        <div style={{ marginBottom: "1rem" }}>
          <label>Message:</label>
          <br />
          <textarea
            rows={8}
            style={{ width: "100%", marginTop: "0.5rem", padding: "0.5rem", fontSize: "0.95rem" }}
            value={welcomeMsg}
            onChange={(e) => setWelcomeMsg(e.target.value)}
            placeholder="e.g. Welcome to Avenues Unlimited! We're looking forward to working with you. Please click the link below to complete your intake forms before your first appointment."
          />
        </div>

        <button onClick={handleSaveWelcomeMessage} disabled={wmSaving}>
          {wmSaving ? "Saving..." : "Save Welcome Message"}
        </button>

        {wmStatus && <p style={{ marginTop: "1rem" }}>{wmStatus}</p>}
      </div>
    </div>
  );
}

export default TherapistToolsPage;
